import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MapView from "@/components/MapView";
import { ArrowLeft, MapPin, Users, Clock, Car, Sparkles, Bus, Loader2, CheckCircle2, Lock, Crown } from "lucide-react";

const PLACES = [
  { name: "ENEC HQ", lat: 24.4539, lng: 54.3773 },
  { name: "Barakah NPP", lat: 23.9686, lng: 52.1925 },
  { name: "Khalifa City", lat: 24.422, lng: 54.578 },
  { name: "Al Raha Beach", lat: 24.4275, lng: 54.6097 },
  { name: "Ruwais Housing", lat: 24.0975, lng: 52.7311 },
  { name: "Yas Marina", lat: 24.4672, lng: 54.6031 },
];

const RIDE_TYPES = [
  { id: "shared", name: "Shared Ride", subtitle: "Grouped with 1–3 colleagues on your route", seats: 1, eta: "5 min", icon: Users, perk: "Greenest · Default", execOnly: false },
  { id: "bus", name: "Next Bus", subtitle: "Scheduled departures", seats: 35, eta: "14:30", icon: Bus, perk: "Most CO₂ saved", execOnly: false },
  { id: "sedan", name: "Solo Sedan", subtitle: "Toyota Camry / Nissan Altima — private", seats: 4, eta: "4 min", icon: Car, perk: "Executive", execOnly: true },
  { id: "suv", name: "Solo SUV", subtitle: "Land Cruiser / Patrol — private", seats: 6, eta: "6 min", icon: Car, perk: "Executive", execOnly: true },
  { id: "electric_car", name: "Tesla EV", subtitle: "Model 3 — private", seats: 4, eta: "8 min", icon: Sparkles, perk: "Executive", execOnly: true },
];

export default function BookRide() {
  const nav = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const isExec = (user?.tier || "standard") === "executive";
  const [step, setStep] = useState(state?.dropoff ? "options" : "destination"); // destination | options | confirming | confirmed
  const [dropoff, setDropoff] = useState(state?.dropoff || null);
  const [pickup] = useState({
    lat: user?.home_lat || 24.422,
    lng: user?.home_lng || 54.578,
    address: user?.home_address || "Khalifa City, Abu Dhabi",
  });
  const [vehicleType, setVehicleType] = useState("shared");
  const [seats, setSeats] = useState(1);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  const filtered = PLACES.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  async function confirm() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        type: vehicleType === "shared" ? "shared" : (vehicleType === "bus" ? "bus_seat" : "ride_now"),
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        pickup_address: pickup.address,
        dropoff_lat: dropoff.lat,
        dropoff_lng: dropoff.lng,
        dropoff_address: dropoff.address || dropoff.name,
        seats_requested: seats,
        vehicle_preference: vehicleType,
      });
      setBooking(data);
      setStep("confirmed");
      setTimeout(() => nav(`/app/track/${data.id}`, { replace: true }), 1600);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------- Destination step ----------------
  if (step === "destination") {
    return (
      <div className="min-h-screen bg-white">
        <div className="px-4 pt-safe pt-6 pb-4 sticky top-0 bg-white z-10 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <button onClick={() => nav(-1)} data-testid="book-back" className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-display font-bold text-xl">Where to?</h1>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 h-12">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-900 ring-4 ring-slate-200" />
              <span className="text-sm text-slate-700 truncate flex-1">{pickup.address}</span>
              <span className="text-xs text-slate-400 font-mono">Pickup</span>
            </div>
            <div className="flex items-center gap-3 border-2 border-primary bg-primary-50 rounded-xl px-4 h-12">
              <div className="w-2.5 h-2.5 rounded-full bg-primary ring-4 ring-primary-200" />
              <input
                autoFocus
                data-testid="book-destination-input"
                value={search || (dropoff?.name || dropoff?.address || "")}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setDropoff(null);
                }}
                placeholder="Search destination…"
                className="flex-1 bg-transparent outline-none text-slate-900 font-medium"
              />
            </div>
          </div>
        </div>

        <div className="px-4 py-2">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-2 mb-2">ENEC Facilities</p>
          {filtered.map((p) => (
            <button
              key={p.name}
              data-testid={`dest-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => {
                setDropoff({ ...p, address: p.name });
                setStep("options");
              }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 text-primary flex items-center justify-center">
                <MapPin size={18} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">Abu Dhabi · UAE</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------------- Options step ----------------
  if (step === "options") {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 relative">
        <div className="absolute inset-0 z-0 h-[55vh]">
          <MapView
            pickup={pickup}
            dropoff={dropoff}
            route={[[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]}
            center={[(pickup.lat + dropoff.lat) / 2, (pickup.lng + dropoff.lng) / 2]}
            zoom={10}
          />
        </div>

        <div className="px-4 pt-safe pt-6 z-10">
          <button onClick={() => setStep("destination")} className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center" data-testid="options-back">
            <ArrowLeft size={20} />
          </button>
        </div>

        <div className="mt-auto bg-white rounded-t-[28px] shadow-2xl z-10 sheet-enter">
          <div className="px-5 pt-5 pb-3">
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Trip</p>
                <p className="font-display font-bold text-lg text-slate-900 truncate">
                  {pickup.address.split(",")[0]} → {dropoff.address}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] uppercase text-slate-400 font-bold tracking-wider">ETA</p>
                <p className="font-display font-bold text-lg text-primary">12 min</p>
              </div>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
              <Sparkles size={12} className="text-emerald-600" />
              <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Complimentary · covered by ENEC</span>
            </div>
          </div>

          <div className="px-5 space-y-2 max-h-[40vh] overflow-y-auto">
            {RIDE_TYPES.map((r) => {
              const sel = vehicleType === r.id;
              const locked = r.execOnly && !isExec;
              return (
                <button
                  key={r.id}
                  data-testid={`ride-option-${r.id}`}
                  onClick={() => {
                    if (locked) {
                      toast.message("Executive tier only", {
                        description: "Solo private rides are reserved for executive employees. Please choose a shared ride or the company bus.",
                      });
                      return;
                    }
                    setVehicleType(r.id);
                  }}
                  disabled={locked}
                  aria-disabled={locked}
                  className={`relative w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                    locked
                      ? "border-slate-100 bg-slate-50/60 opacity-70 cursor-not-allowed"
                      : sel
                        ? "border-primary bg-primary-50/50 shadow-sm"
                        : "border-slate-100 bg-white hover:border-slate-200"
                  }`}
                >
                  <div className={`w-14 h-14 rounded-xl ${sel && !locked ? "bg-primary text-white" : locked ? "bg-slate-200 text-slate-400" : "bg-slate-100 text-slate-700"} flex items-center justify-center transition-colors shrink-0`}>
                    <r.icon size={26} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-slate-900">{r.name}</p>
                      {!locked && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase">
                          <Clock size={11} /> {r.eta}
                        </span>
                      )}
                      {locked && (
                        <span data-testid={`ride-locked-${r.id}`} className="flex items-center gap-1 text-[10px] font-bold text-amber-700 uppercase tracking-wider bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          <Lock size={10} /> Executive only
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {locked ? (
                      <Crown size={20} className="text-amber-500 ml-auto" />
                    ) : (
                      <>
                        <p className="font-display font-bold text-emerald-600 text-sm">Complimentary</p>
                        <p className="text-[10px] text-slate-400 font-semibold uppercase">{r.seats} seats · {r.perk}</p>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
            {!isExec && (
              <div data-testid="tier-hint" className="mt-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900">
                <p className="font-bold">You&apos;re sharing the ride 🌿</p>
                <p className="mt-0.5 text-emerald-800">ENEC groups you with colleagues on your route — saving CO₂ and roads. Solo private cars are reserved for executive tier.</p>
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-slate-100 mt-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-600">Seats needed</span>
              <div className="flex items-center gap-3">
                <button data-testid="seats-minus" onClick={() => setSeats(Math.max(1, seats - 1))} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 font-bold">−</button>
                <span className="font-display font-bold text-lg w-6 text-center" data-testid="seats-value">{seats}</span>
                <button data-testid="seats-plus" onClick={() => setSeats(Math.min(6, seats + 1))} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 font-bold">+</button>
              </div>
            </div>

            <button
              data-testid="book-confirm"
              onClick={confirm}
              disabled={submitting}
              className="w-full h-14 rounded-xl bg-primary hover:bg-primary-600 disabled:opacity-60 text-white font-display font-bold text-lg flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 size={20} className="animate-spin" /> : (<>Book Now<Sparkles size={18} /></>)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- Confirmed step ----------------
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6 fade-up">
      <div className="text-center max-w-sm">
        <div className="w-24 h-24 mx-auto rounded-full bg-primary-50 flex items-center justify-center mb-4">
          <CheckCircle2 size={56} className="text-primary" />
        </div>
        <h1 className="font-display font-bold text-3xl text-slate-900">Ride Booked</h1>
        <p className="text-slate-500 mt-2">We&apos;re finding the closest available driver for you.</p>
        <p className="font-mono text-xs text-primary mt-4 bg-primary-50 inline-block px-3 py-1.5 rounded-full" data-testid="booking-ref">
          {booking?.ref}
        </p>
        <p className="text-xs text-slate-400 mt-6 dot-loading">Tracking</p>
      </div>
    </div>
  );
}
